








































import Vue from "vue";
import Component from "vue-class-component";
import { Prop, Watch } from "vue-property-decorator";
import { ContentSet, InputContentSetUpdate } from "@scrile/api-provider/dist/api/ContentSetProvider";
import { ContentFeedTypeEnum } from "@scrile/api-provider/dist/projects/scrileConnect/ContentFeedProvider";
import { titleForContentType } from "@/lib/contentFeedHelper";
import { Debounce } from "@/lib/decorators";
import { $t } from "@/lib/i18nHelpers";
import { PostFileData } from "@/types";
import { MAX_PHOTO_GALLERY_FILES, MIN_PHOTO_GALLERY_FILES } from "@/const";
import BaseIcon from "@/components/BaseIcon";
import CustomScroll from "@/components/CustomScroll";
import PageTitle from "@/components/PageTitle";
import ClipEditView from "../ClipEditView";
import EditPostDropZone from "../EditPostDropZone";
import EditPostOptions from "../EditPostOptions";
import EventPostEditView from "../EventPostEditView";
import InputWithMentions from "../InputWithMetions";
import MediaPostEditView from "../MediaPostEditView";
import PreviewSetupController from "../PreviewSetupController";
import PriceSetupController from "../PriceSetupController";
import SchedulePostController from "../SchedulePostController";
import TagUserController from "../TagUserController";
import VaultFilesAdapter from "../VaultFilesAdapter";

@Component({
  name: "EditPostOverview",
  components: {
    EditPostDropZone,
    SchedulePostController,
    PreviewSetupController,
    PriceSetupController,
    EditPostOptions,
    PageTitle,
    BaseIcon,
    InputWithMentions,
    CustomScroll,
    VaultFilesAdapter,
    TagUserController,
  },
})
export default class EditPostOverview extends Vue {
  @Prop({ required: true })
  public readonly postData!: ContentSet;

  @Prop({ required: true })
  public readonly fileList!: PostFileData[];

  public description = this.postData?.description ?? "";

  public taggedUserIds = this.postData?.taggedUserIds ?? [];

  public get maxFiles() {
    if (this.postData && this.postData.type === ContentFeedTypeEnum.CLIP) return 1;
    if (this.postData && this.postData.type === ContentFeedTypeEnum.PHOTO_GALLERY) return MAX_PHOTO_GALLERY_FILES;
    return undefined;
  }

  public get minFiles() {
    if (this.postData && this.postData.type === ContentFeedTypeEnum.PHOTO_GALLERY) return MIN_PHOTO_GALLERY_FILES;
    return undefined;
  }

  public get disableSubmitButton() {
    return !!this.minFiles && this.fileList.length < this.minFiles;
  }

  public get isEditing() {
    return this.postData.published;
  }

  public get allowedFileTypes() {
    if (this.postData && this.postData.type === ContentFeedTypeEnum.CLIP) return ["video"];
    if (this.postData && this.postData.type === ContentFeedTypeEnum.PHOTO_GALLERY) return ["image"];
    return undefined;
  }

  public get maxCaptionLength() {
    if (
      this.postData &&
      (this.postData.type === ContentFeedTypeEnum.CLIP || this.postData.type === ContentFeedTypeEnum.PHOTO_GALLERY)
    )
      return "55";
  }

  public get type() {
    if (!this.postData) return "";
    return this.postData.type;
  }

  public get component() {
    if (this.type === ContentFeedTypeEnum.EVENT) return EventPostEditView;
    if (this.type === ContentFeedTypeEnum.CLIP) return ClipEditView;
    return MediaPostEditView;
  }

  public forceShowVault = false;

  public get showVault() {
    return this.type !== ContentFeedTypeEnum.EVENT && (this.forceShowVault || this.fileList.length === 0);
  }

  public get title() {
    const postName = titleForContentType[this.postData.type];
    return this.isEditing ? $t("Edit {0}", [postName]) : $t("New {0}", [postName]);
  }

  @Debounce(1000, { leading: false })
  @Watch("description")
  public watchDescription(description: string) {
    const data: Omit<InputContentSetUpdate, "id"> = {
      description,
    };
    this.$emit("update", { data });
  }

  @Watch("taggedUserIds")
  public watchTaggedUserIds(taggedUserIds: string[]) {
    const data: Omit<InputContentSetUpdate, "id"> = {
      taggedUserIds,
    };
    this.$emit("update", { data });
  }

  @Watch("fileList")
  public watchFileList(fileList: PostFileData[]) {
    if (fileList.length === 0) {
      this.taggedUserIds = [];
    }
  }

  public onFileRemove(fileId: string) {
    this.$emit("fileRemove", fileId, { isEditing: this.isEditing });
  }

  public onPublish(cb: Function) {
    const data: Omit<InputContentSetUpdate, "id"> = {
      description: this.description,
      taggedUserIds: this.taggedUserIds,
      published: true,
    };
    this.$emit("update", { data, closeOnSave: true }, cb);
  }

  public onBack() {
    if (this.showVault && this.forceShowVault) {
      this.forceShowVault = false;
      this.showVault && this.onBack();
    } else {
      this.$emit("back");
    }
  }
}
